import React from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';

const ValueDiff = ({
  previousValue,
  newValue,
  title = 'Changes',
  maxHeight = '300px',
}) => {
  if (!previousValue && !newValue) {
    return null;
  }

  if (JSON.stringify(previousValue) === JSON.stringify(newValue)) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          No changes detected
        </Typography>
      </Box>
    );
  }

  if (typeof previousValue !== 'object' || typeof newValue !== 'object') {
    return (
      <SimpleComparison
        previousValue={previousValue}
        newValue={newValue}
        title={title}
      />
    );
  }

  return (
    <InlineComparison
      previousValue={previousValue}
      newValue={newValue}
      title={title}
      maxHeight={maxHeight}
    />
  );
};

const SimpleComparison = ({ previousValue, newValue, title }) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 'bold' }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        {previousValue !== undefined && previousValue !== null && (
          <Paper
            sx={{
              flex: 1,
              p: 2,
              bgcolor: 'error.50',
              border: '1px solid',
              borderColor: 'error.200',
            }}>
            <Typography
              variant='caption'
              sx={{ fontWeight: 'bold', color: 'error.dark' }}>
              PREVIOUS
            </Typography>
            <Typography variant='body2' sx={{ mt: 1, fontFamily: 'monospace' }}>
              {String(previousValue)}
            </Typography>
          </Paper>
        )}
        {newValue !== undefined && newValue !== null && (
          <Paper
            sx={{
              flex: 1,
              p: 2,
              bgcolor: 'success.50',
              border: '1px solid',
              borderColor: 'success.200',
            }}>
            <Typography
              variant='caption'
              sx={{ fontWeight: 'bold', color: 'success.dark' }}>
              NEW
            </Typography>
            <Typography variant='body2' sx={{ mt: 1, fontFamily: 'monospace' }}>
              {String(newValue)}
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

const InlineComparison = ({ previousValue, newValue, title, maxHeight }) => {
  const changes = getObjectDifferences(previousValue, newValue);

  if (changes.length === 0) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          No structural changes detected
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 'bold' }}>
        {title}
      </Typography>
      <Box
        sx={{
          maxHeight,
          overflow: 'auto',
          bgcolor: 'grey.50',
          p: 1,
          borderRadius: 1,
        }}>
        {changes.map((change, index) => (
          <ChangeItem key={index} change={change} />
        ))}
      </Box>
    </Box>
  );
};

const ChangeItem = ({ change }) => {
  return (
    <Box sx={{ mb: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Chip
        size='small'
        label={change.type}
        color={
          change.type === 'added'
            ? 'success'
            : change.type === 'removed'
            ? 'error'
            : 'info'
        }
        sx={{ fontSize: '0.7rem', height: 20, minWidth: 60 }}
      />
      <Box sx={{ flex: 1 }}>
        <Typography
          variant='body2'
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.8rem',
            fontWeight: 'bold',
            mb: 0.5,
          }}>
          {change.path}
        </Typography>
        <Typography
          variant='body2'
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            color: 'text.secondary',
            wordBreak: 'break-all',
          }}>
          {change.description}
        </Typography>
      </Box>
    </Box>
  );
};

const getObjectDifferences = (prev, curr) => {
  const changes = [];
  const prevObj = prev || {};
  const currObj = curr || {};

  const allKeys = new Set([...Object.keys(prevObj), ...Object.keys(currObj)]);

  allKeys.forEach((key) => {
    const prevVal = prevObj[key];
    const currVal = currObj[key];

    if (prevVal === undefined && currVal !== undefined) {
      changes.push({
        type: 'added',
        path: key,
        description: formatValue(currVal),
      });
    } else if (prevVal !== undefined && currVal === undefined) {
      changes.push({
        type: 'removed',
        path: key,
        description: formatValue(prevVal),
      });
    } else if (JSON.stringify(prevVal) !== JSON.stringify(currVal)) {
      if (
        typeof prevVal === 'object' &&
        typeof currVal === 'object' &&
        prevVal !== null &&
        currVal !== null
      ) {
        const nestedChanges = getObjectDifferences(prevVal, currVal);
        nestedChanges.forEach((nestedChange) => {
          changes.push({
            ...nestedChange,
            path: `${key}.${nestedChange.path}`,
          });
        });
      } else {
        changes.push({
          type: 'modified',
          path: key,
          description: `${formatValue(prevVal)} → ${formatValue(currVal)}`,
        });
      }
    }
  });

  return changes;
};

const formatValue = (value) => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return `"${value}"`;

  return JSON.stringify(value);
};

export default ValueDiff;
